import { type ComponentPropsWithoutRef, type ReactElement, type ReactNode, useState } from 'react';

import { DropdownMenu, DropdownMenuTrigger } from '@crossnokaye/ux-common';

import { Tooltip, TooltipTrigger } from '@/ux-common/components/tooltip/tooltip';

import { NavTooltipContent } from './nav-tooltip-content';

export interface NavTooltipDropdownTriggerProps {
  /** The menu — a `DropdownMenuContent` (plus anything else the menu root hosts). */
  children: ReactNode;
  /**
   * Extra props for the `DropdownMenuTrigger` (e.g. `disabled`, a propagation
   * guard). They merge onto the same element as everything else via `asChild` —
   * which is why `asChild` and `children` are carved out: the composition owns
   * both, and a caller's bag must not be able to flip the composition mode.
   */
  dropdownTriggerProps?: Omit<
    ComponentPropsWithoutRef<typeof DropdownMenuTrigger>,
    'asChild' | 'children'
  >;
  /** The nav tooltip's text. */
  label: string;
  /** Menu open state — lifted, because consumers style the trigger by it. */
  menuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
  /** Holds the tooltip shut regardless of hover (e.g. the picker is disabled). */
  tooltipDisabled?: boolean;
  /**
   * The element both Radix triggers compose onto — a single element that
   * forwards its ref to a DOM node. `ReactElement`, not `ReactNode`: a string,
   * fragment or array compiles against `ReactNode` and then fails at runtime
   * inside Radix's Slot, so the contract is enforced where it's cheap.
   */
  trigger: ReactElement;
}

/**
 * A nav trigger that is simultaneously a tooltip anchor and a dropdown-menu
 * anchor. Radix's Tooltip and DropdownMenu do not coordinate on a shared
 * trigger, so this composition owns the dance once:
 *
 * - The tooltip is fully controlled and never handed back to Radix: flipping
 *   between a controlled `false` and `undefined` would freeze Radix's internal
 *   open state while controlled, and newer Radix dev-warns on the switch.
 * - It is held shut while the menu is open (or `tooltipDisabled`).
 * - Opening or closing the menu consumes the hover: Radix keeps reporting open
 *   intent behind the shut gate, and without the reset the tooltip pops the
 *   moment the menu closes under the pointer.
 * - The tooltip trigger lives INSIDE the menu trigger, so both compose onto the
 *   caller's one element via `asChild` — which is why `trigger` must forward a
 *   ref.
 */
export const NavTooltipDropdownTrigger = ({
  children,
  dropdownTriggerProps,
  label,
  menuOpen,
  onMenuOpenChange,
  tooltipDisabled,
  trigger,
}: NavTooltipDropdownTriggerProps) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <Tooltip open={menuOpen || tooltipDisabled ? false : showTooltip} onOpenChange={setShowTooltip}>
      <DropdownMenu
        open={menuOpen}
        onOpenChange={(open) => {
          onMenuOpenChange(open);
          setShowTooltip(false);
        }}
      >
        <DropdownMenuTrigger {...dropdownTriggerProps} asChild>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        </DropdownMenuTrigger>
        {children}
      </DropdownMenu>
      <NavTooltipContent label={label} />
    </Tooltip>
  );
};
